import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  useDialogInboxDetail,
  useIgnoreDialogInbox,
  useProcessDialogInbox,
  useReopenDialogInbox,
} from '../hooks/reactQuerys/DialogInboxQueries';
import { useGetLegalRoleTemplates } from '../hooks/reactQuerys/LegalRoleQueries';
import {
  useGetLegalTransactionProgresses,
  useGetLegalTransactionStates,
  useGetLegalTransactionTypes,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import {
  useGetUserGroups,
  useGetUsers,
} from '../hooks/reactQuerys/UserQueries';
import { useGetOpenFormulareDialogs } from '../hooks/reactQuerys/OpenFormulareDialogQueries';
import {
  IParticipantDecision,
  IRealEstateDecision,
} from '../Interfaces/IDialogInbox';
import { useAlert } from '../context/AlertContext';
import { DialogInfoSection } from './Childs/DialogInfoSection';
import { ParticipantsSection } from './Childs/ParticipantsSection';
import { ClientMatchSection } from './Childs/ClientMatchSection';
import { RealEstateMatchSection } from './Childs/RealEstateMatchSection';
import { ACTION_LABEL, ACTION_LABEL_REAL_ESTATE, labelForStatus } from './i18n';
import {
  ILegalTransactionTypeSuggestion,
  suggestLegalTransactionTypes,
} from './legalTransactionTypeMatcher';
import {
  findLegalRoleTemplateByName,
  findUserByName,
  findUserGroupByName,
} from './userMatcher';
import {
  ITransactionHeaderValue,
  TransactionHeaderSection,
} from './Childs/TransactionHeaderSection';

interface Props {
  idDialogInbox: number;
  onClose: () => void;
}

export const DialogInboxDetail: React.FC<Props> = ({
  idDialogInbox,
  onClose,
}) => {
  const { showAlert } = useAlert();
  const { dialogInboxDetail, isLoadingDialogInboxDetail } =
    useDialogInboxDetail(idDialogInbox);
  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();
  const { roleTemplates } = useGetLegalRoleTemplates();
  const { processDialogInbox, isProcessing } = useProcessDialogInbox();
  const { ignoreDialogInbox, isIgnoring } = useIgnoreDialogInbox();

  const [selectedTempId, setSelectedTempId] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<
    Record<string, IParticipantDecision>
  >({});
  const [rolesByTempId, setRolesByTempId] = useState<
    Record<string, number | undefined>
  >({});
  const [realEstateDecision, setRealEstateDecision] = useState<
    IRealEstateDecision | undefined
  >(undefined);
  const [idLegalTransactionType, setIdLegalTransactionType] = useState<
    number | ''
  >('');
  const [hasUserPickedType, setHasUserPickedType] = useState(false);
  const [usePhasesFromImport, setUsePhasesFromImport] = useState(true);

  const { users } = useGetUsers();
  const { userGroups } = useGetUserGroups();
  const { ofDialogs } = useGetOpenFormulareDialogs();
  const { legalTransactionStates } = useGetLegalTransactionStates();
  const { legalTransactionProgresses } = useGetLegalTransactionProgresses();

  // Im Admin-Bereich "Dialoge" hinterlegte Konfiguration zum eingehenden
  // Dialog (Match ueber FormType == OF-DialogId). Liefert die fest
  // vorkonfigurierte Vorgangsart + Notar/Mitarbeiter/Gruppe. Hat Vorrang
  // vor dem Fuzzy-Vorschlag; ist auch ohne erreichbares OF verfuegbar, da
  // die Config lokal in DiNo gespeichert wird.
  const dialogConfig = useMemo(() => {
    const formType = dialogInboxDetail?.FormType;
    if (!formType || !ofDialogs?.dialogs) return null;
    return ofDialogs.dialogs.find(d => d.DialogId === formType)?.config ?? null;
  }, [dialogInboxDetail?.FormType, ofDialogs]);
  const [headerEdited, setHeaderEdited] = useState(false);
  const [header, setHeader] = useState<ITransactionHeaderValue>({
    title: '',
    notaryIdUser: null,
    employeeIdUser: null,
    userGroupId: null,
    notarizationDate: '',
    // Defaults: Status "Neu" (1), Phase "Datenerfassung" (10) — Pflichtfelder.
    legalTransactionStateId: 1,
    legalTransactionProgressId: 10,
    recordNumbers: [],
    transactionNumbers: [],
  });

  const [mode, setMode] = useState<'transaction' | 'clientsOnly'>(
    'transaction'
  );
  const [modeAutoSet, setModeAutoSet] = useState(false);

  // Vorgangsart-Vorschlaege aus dem ``transactionTypeHint`` ableiten.
  const transactionTypeHint =
    dialogInboxDetail?.TransactionTypeHint ??
    dialogInboxDetail?.Data?.transactionTypeHint ??
    null;
  // Zusaetzliche Quelle: der Dialogname (z.B. "Allgemeines Anliegen"). Bei
  // OF-Dialogen ist kein transactionTypeHint gesetzt — der Dialogname ist aber
  // oft namensgleich zur Vorgangsart. So greift der Vorschlag fuer JEDEN
  // Import-Typ (OF wie strukturiert).
  const dialogNameHint =
    dialogInboxDetail?.DialogTitle ??
    dialogInboxDetail?.Title ??
    dialogInboxDetail?.Data?.dialogTitle ??
    null;
  const typeSuggestions: ILegalTransactionTypeSuggestion[] = useMemo(() => {
    // Vorschlaege aus Hint UND Dialogname zusammenfuehren; pro Vorgangsart den
    // hoechsten Score behalten, dann absteigend sortieren.
    const byId = new Map<number, ILegalTransactionTypeSuggestion>();
    for (const s of [
      ...suggestLegalTransactionTypes(
        transactionTypeHint,
        legalTransactionTypes
      ),
      ...suggestLegalTransactionTypes(dialogNameHint, legalTransactionTypes),
    ]) {
      const id = s.type.idLegalTransactionType;
      const prev = byId.get(id);
      if (!prev || s.score > prev.score) byId.set(id, s);
    }
    return Array.from(byId.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [transactionTypeHint, dialogNameHint, legalTransactionTypes]);

  // "Vorgangs-Kontext" = Importdatei enthaelt Vorgangsdaten (Hint, Container,
  // DiNo-Vorgang). Sonst (z.B. echter HR-Auszug) → Default-Modus "Nur Mandanten".
  const hasTransactionContext = useMemo(() => {
    if (!dialogInboxDetail) return false;
    if (transactionTypeHint) return true;
    const payload = dialogInboxDetail.Data ?? {};
    if (payload.formType === 'dino_legaltransaction') return true;
    const ctx = (payload.context ?? {}) as Record<string, unknown>;
    if (ctx.dinoSource) return true;
    const rd = (payload.rawData ?? {}) as Record<string, unknown>;
    const xj = rd.xjustiz as { dinoContainer?: unknown } | undefined;
    if (xj?.dinoContainer) return true;
    return false;
  }, [dialogInboxDetail, transactionTypeHint]);

  // DiNo-Vorgangs-Import erkennen — wird in mehreren useEffects + im Render
  // gebraucht, daher schon hier ableiten.
  const isDinoLegalTransactionImport =
    dialogInboxDetail?.Data?.formType === 'dino_legaltransaction';

  // Default-Modus einmalig setzen, sobald wir wissen, ob Vorgangsdaten da sind.
  // DiNo-Vorgangsuebernahme erzwingt 'transaction' (Mandanten-Only ist hier
  // semantisch sinnlos, da der Import explizit einen Vorgang transportiert).
  useEffect(() => {
    if (modeAutoSet || !dialogInboxDetail) return;
    if (isDinoLegalTransactionImport) {
      setMode('transaction');
    } else {
      setMode(hasTransactionContext ? 'transaction' : 'clientsOnly');
    }
    setModeAutoSet(true);
  }, [
    dialogInboxDetail,
    hasTransactionContext,
    isDinoLegalTransactionImport,
    modeAutoSet,
  ]);

  // Beim ersten Laden die Vorgangsart vorausfuellen — nur, solange der
  // User nichts manuell ausgewaehlt hat.
  //   1. Im Admin hinterlegte Dialog-Config (explizite Zuordnung) hat Vorrang.
  //   2. Fallback: bester Fuzzy-Treffer aus dem ``transactionTypeHint``.
  useEffect(() => {
    if (hasUserPickedType) return;
    if (idLegalTransactionType !== '') return;
    if (dialogConfig?.idLegalTransactionType) {
      setIdLegalTransactionType(dialogConfig.idLegalTransactionType);
      return;
    }
    const best = typeSuggestions[0]?.type;
    if (best) {
      setIdLegalTransactionType(best.idLegalTransactionType);
    }
  }, [
    dialogConfig,
    typeSuggestions,
    hasUserPickedType,
    idLegalTransactionType,
  ]);

  // Vorgangskopf aus dem Payload vorbelegen (nur solange der User noch
  // nichts editiert hat). Quellen je nach Import-Typ:
  //   - XJustiz: payload.context.dinoSource (aus dem Container)
  //   - SDS:     payload.context (basicData via Importer in summary, hier
  //              greifen wir auf rohe Felder zurueck)
  //   - DiNo:    payload.rawData.dino.fullObject
  useEffect(() => {
    if (headerEdited) return;
    if (!dialogInboxDetail) return;

    const payload = dialogInboxDetail.Data ?? {};
    const ctx = (payload.context ?? {}) as Record<string, unknown>;
    const dinoSource = (ctx.dinoSource ?? {}) as Record<string, string>;
    const dinoFullObject = ((payload.rawData ?? {}) as Record<string, unknown>)
      .dino as { fullObject?: Record<string, unknown> } | undefined;
    const dinoLt = (dinoFullObject?.fullObject ?? {}) as Record<
      string,
      unknown
    >;

    const title =
      (dinoLt.Title as string) ||
      dinoSource.Titel ||
      payload.title ||
      dialogInboxDetail.Title ||
      '';

    // Beurkundungsdatum: DiNo-LT > SDS-basicData > XJustiz-Container
    const ndRaw = (dinoLt.NotarizationDate as string) || dinoSource.ND || '';
    const notarizationDate = ndRaw ? String(ndRaw).slice(0, 10) : '';

    // Urkundennummern (UVZ)
    const recFromDino = Array.isArray(dinoLt.RecordNumberArray)
      ? (dinoLt.RecordNumberArray as Array<{ RecordNumberEntry?: string }>)
          .map(r => (r.RecordNumberEntry ?? '').trim())
          .filter(Boolean)
      : [];
    const recFromContainer = dinoSource.UVZ
      ? dinoSource.UVZ.split(',')
          .map(s => s.trim())
          .filter(Boolean)
      : [];
    const recordNumbers = recFromDino.length ? recFromDino : recFromContainer;

    // Aktenzeichen / Transaktionsnummern
    const tnFromDino = Array.isArray(dinoLt.TransactionNumberArray)
      ? (
          dinoLt.TransactionNumberArray as Array<{
            TransactionNumberEntry?: string;
          }>
        )
          .map(r => (r.TransactionNumberEntry ?? '').trim())
          .filter(Boolean)
      : [];
    const tnFromContainer = dinoSource.Akte
      ? dinoSource.Akte.split(',')
          .map(s => s.trim())
          .filter(Boolean)
      : [];
    const transactionNumbers = tnFromDino.length ? tnFromDino : tnFromContainer;

    // Notar/Mitarbeiter aus aktueller Session vorbelegen — und falls der
    // Import einen Display-Namen mitliefert, per Fuzzy-Match auf einen
    // lokalen User mappen (Quell-IDs sind auf der Zielmaschine nicht gueltig).
    const idUserSession = Number(localStorage.getItem('idUser')) || null;
    const sessionUser = (users ?? []).find(u => u.idUser === idUserSession);
    const isCurrentUserNotary = !!sessionUser?.isNotary;
    const onlyNotary = (users ?? []).filter(u => u.isNotary && !u.isBlocked);

    const notaryDisplay = (dinoLt.Notary_Display as string) || null;
    const employeeDisplay = (dinoLt.Employee_Display as string) || null;
    const matchedNotary = findUserByName(
      notaryDisplay,
      (users ?? []).filter(u => u.isNotary)
    );
    const matchedEmployee = findUserByName(
      employeeDisplay,
      (users ?? []).filter(u => !u.isNotary)
    );
    const matchedGroup = findUserGroupByName(
      (dinoLt.UserGroup_Display as string) || null,
      userGroups
    );

    // Im Admin hinterlegte Dialog-Config hat Vorrang vor Fuzzy-Match/Session.
    const defaultNotary =
      dialogConfig?.Notary_idUser ??
      matchedNotary?.idUser ??
      (isCurrentUserNotary ? idUserSession : null) ??
      (onlyNotary.length === 1 ? onlyNotary[0].idUser : null);

    const defaultEmployee =
      dialogConfig?.Employee_idUser ??
      matchedEmployee?.idUser ??
      (!isCurrentUserNotary && idUserSession ? idUserSession : null);

    // Status + Phase aus dem Import — beim DiNo-Export sind die IDs ueber
    // alle Instanzen gleich (Stammdaten). Defaults: Status "Neu" (1) und
    // Phase "Datenerfassung" (10), wenn der Import keine Werte liefert.
    const STATE_NEU = 1;
    const PROGRESS_DATENERFASSUNG = 10;
    const importedStateId =
      typeof dinoLt.idLegalTransactionState === 'number'
        ? (dinoLt.idLegalTransactionState as number)
        : STATE_NEU;
    const importedProgressId =
      typeof dinoLt.idLegalTransactionProgress === 'number'
        ? (dinoLt.idLegalTransactionProgress as number)
        : PROGRESS_DATENERFASSUNG;

    setHeader({
      title,
      notaryIdUser: defaultNotary,
      employeeIdUser: defaultEmployee,
      userGroupId:
        dialogConfig?.UserGroup_idUserGroup ??
        matchedGroup?.idUserGroup ??
        null,
      notarizationDate,
      legalTransactionStateId: importedStateId,
      legalTransactionProgressId: importedProgressId,
      recordNumbers,
      transactionNumbers,
    });
  }, [dialogInboxDetail, users, userGroups, headerEdited, dialogConfig]);

  const availableRoleTemplates = useMemo(() => {
    if (!idLegalTransactionType) return [];
    return (roleTemplates ?? []).filter(
      rt => rt.idLegalTransactionType === idLegalTransactionType
    );
  }, [roleTemplates, idLegalTransactionType]);

  const isReadOnly =
    dialogInboxDetail !== undefined && dialogInboxDetail.Status !== 'pending';

  const participants = dialogInboxDetail?.Data?.legalClients ?? [];

  // Beteiligten-Rollen aus dem Import auf die LegalRoleTemplates der gewaehlten
  // Vorgangsart matchen. Pro Mandant wird die Klartextrolle (`participant.role`)
  // fuzzy gegen `LegalRoleTemplate` verglichen — ist ein Treffer da, wird die
  // Rolle vorausgewaehlt. User-Picks (im UI) werden nicht ueberschrieben.
  useEffect(() => {
    if (isReadOnly) return;
    if (availableRoleTemplates.length === 0) return;
    if (participants.length === 0) return;
    setRolesByTempId(prev => {
      const next = { ...prev };
      let changed = false;
      for (const p of participants) {
        if (next[p.tempId] !== undefined) continue;
        const matched = findLegalRoleTemplateByName(
          p.role,
          availableRoleTemplates
        );
        if (matched) {
          next[p.tempId] = matched.idLegalRoleTemplate;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [participants, availableRoleTemplates, isReadOnly]);
  // Memoisiert, damit die Objekt-Referenz stabil bleibt. Sonst entsteht bei
  // jedem Render ein neues realEstate-Objekt, was den Such-useEffect in
  // RealEstateMatchSection ständig neu auslöst und die Auswahl zurücksetzt.
  const dialogRealEstate = useMemo(
    () =>
      (
        dialogInboxDetail?.Data?.context as
          | { realEstate?: Record<string, unknown> }
          | undefined
      )?.realEstate as
        | Parameters<typeof RealEstateMatchSection>[0]['realEstate']
        | undefined,
    [dialogInboxDetail]
  );

  const allDecided = useMemo(
    () =>
      participants.every(p => decisions[p.tempId]) &&
      (!dialogRealEstate || realEstateDecision !== undefined),
    [participants, decisions, dialogRealEstate, realEstateDecision]
  );
  const hasAtLeastOneClient = useMemo(
    () =>
      Object.values(decisions).some(d =>
        ['use', 'merge', 'create'].includes(d.action)
      ),
    [decisions]
  );
  const allRolesAssigned = useMemo(() => {
    // Wenn die gewählte Vorgangsart keine LegalRoleTemplates hat,
    // entfällt die Rollen-Pflicht — sonst wäre "Vorgang erstellen"
    // unmöglich, weil das Dropdown leer & deaktiviert ist.
    if (availableRoleTemplates.length === 0) return true;
    return participants.every(p => {
      const decision = decisions[p.tempId];
      if (!decision || decision.action === 'ignore') return true;
      return Boolean(rolesByTempId[p.tempId]);
    });
  }, [participants, decisions, rolesByTempId, availableRoleTemplates]);

  const selectedParticipant = participants.find(
    p => p.tempId === selectedTempId
  );

  const handleDecide = (decision: IParticipantDecision) => {
    setDecisions(prev => ({ ...prev, [decision.tempId]: decision }));
    showAlert({
      severity: 'info',
      text: `Entscheidung übernommen (${ACTION_LABEL[decision.action]}). Sie können sie jederzeit bis zum Anlegen des Vorgangs noch ändern.`,
    });
    // automatisch zum nächsten offenen Beteiligten wechseln
    const next = participants.find(
      p =>
        p.tempId !== decision.tempId &&
        !decisions[p.tempId] &&
        p.tempId !== selectedTempId
    );
    if (next) setSelectedTempId(next.tempId);
  };

  const handleResetDecision = (tempId: string) => {
    setDecisions(prev => {
      const next = { ...prev };
      delete next[tempId];
      return next;
    });
    setRolesByTempId(prev => {
      const next = { ...prev };
      delete next[tempId];
      return next;
    });
  };

  const handleProcess = async () => {
    if (mode === 'transaction' && !idLegalTransactionType) return;
    try {
      const enrichedDecisions: IParticipantDecision[] = Object.values(
        decisions
      ).map(d => {
        if (d.action === 'ignore') return d;
        const idLegalRoleTemplate = rolesByTempId[d.tempId];
        return idLegalRoleTemplate ? { ...d, idLegalRoleTemplate } : d;
      });
      await processDialogInbox({
        idDialogInbox,
        body: {
          mode,
          ...(mode === 'transaction'
            ? {
                idLegalTransactionType: idLegalTransactionType as number,
                realEstate: dialogRealEstate ? realEstateDecision : undefined,
                ...(isDinoLegalTransactionImport
                  ? { usePhasesFromImport }
                  : {}),
                header: {
                  title: header.title.trim() || undefined,
                  notaryIdUser: header.notaryIdUser ?? undefined,
                  employeeIdUser: header.employeeIdUser ?? undefined,
                  userGroupId: header.userGroupId ?? undefined,
                  notarizationDate: header.notarizationDate || undefined,
                  legalTransactionStateId:
                    header.legalTransactionStateId ?? undefined,
                  legalTransactionProgressId:
                    header.legalTransactionProgressId ?? undefined,
                  recordNumbers: header.recordNumbers.length
                    ? header.recordNumbers
                    : undefined,
                  transactionNumbers: header.transactionNumbers.length
                    ? header.transactionNumbers
                    : undefined,
                },
              }
            : {}),
          participants: enrichedDecisions,
        },
      });
      showAlert({
        severity: 'success',
        text:
          mode === 'clientsOnly' ? 'Mandanten angelegt' : 'Vorgang erstellt',
      });
      onClose();
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          mode === 'clientsOnly'
            ? 'Fehler beim Anlegen der Mandanten'
            : 'Fehler beim Erstellen des Vorgangs',
      });
    }
  };

  const [confirmIgnoreOpen, setConfirmIgnoreOpen] = useState(false);

  const handleIgnoreAll = async () => {
    setConfirmIgnoreOpen(false);
    try {
      await ignoreDialogInbox(idDialogInbox);
      showAlert({ severity: 'success', text: 'Dialog ignoriert' });
      onClose();
    } catch {
      showAlert({ severity: 'error', text: 'Fehler beim Ignorieren' });
    }
  };

  const { reopenDialogInbox, isReopening } = useReopenDialogInbox();
  const handleReopen = async () => {
    try {
      await reopenDialogInbox(idDialogInbox);
      showAlert({
        severity: 'success',
        text: 'Dialog wieder geöffnet — Sie können ihn jetzt erneut verarbeiten.',
      });
    } catch {
      showAlert({
        severity: 'error',
        text: 'Wiederöffnen fehlgeschlagen.',
      });
    }
  };

  return (
    <>
      <Dialog open onClose={onClose} maxWidth='lg' fullWidth>
        <DialogTitle>Importbereich</DialogTitle>
        <DialogContent dividers>
          {isLoadingDialogInboxDetail || !dialogInboxDetail ? (
            <CircularProgress />
          ) : (
            <Stack spacing={2}>
              {isReadOnly && (
                <Alert severity='info'>
                  Status: {labelForStatus(dialogInboxDetail.Status)} — eine
                  Bearbeitung ist nicht mehr möglich.
                </Alert>
              )}
              <DialogInfoSection detail={dialogInboxDetail} />

              {/*
              Vorgangsanlage: Modus-Toggle + Vorgangsart + Vorgangskopf.
              Bei mode='clientsOnly' werden die Vorgangs-Felder ausgeblendet.
            */}
              <TransactionHeaderSection
                mode={mode}
                onModeChange={newMode => {
                  setMode(newMode);
                  setModeAutoSet(true);
                }}
                hasTransactionContext={hasTransactionContext}
                hideModeToggle={isDinoLegalTransactionImport}
                legalTransactionTypes={legalTransactionTypes}
                idLegalTransactionType={idLegalTransactionType}
                setIdLegalTransactionType={v => {
                  setIdLegalTransactionType(v);
                  setRolesByTempId({});
                }}
                transactionTypeHint={transactionTypeHint}
                typeSuggestions={typeSuggestions}
                onUserPickedType={() => setHasUserPickedType(true)}
                isLoadingLegalTransactionTypes={isLoadingLegalTransactionTypes}
                value={header}
                onChange={v => {
                  setHeader(v);
                  setHeaderEdited(true);
                }}
                users={users ?? []}
                userGroups={userGroups ?? []}
                legalTransactionStates={legalTransactionStates ?? []}
                legalTransactionProgresses={legalTransactionProgresses ?? []}
                disabled={isReadOnly}
              />
              {/* Alte Vorgangsart-/Header-Sektionen sind in TransactionHeaderSection oben aufgegangen. */}

              {isDinoLegalTransactionImport && (
                <Paper variant='outlined' sx={{ p: 2 }}>
                  <Alert severity='info' sx={{ mb: 2 }}>
                    <AlertTitle>DiNo-Vorgangsübernahme</AlertTitle>
                    Dieser Eintrag stammt aus einem DiNo-Export. Beim Erstellen
                    wird der komplette Vorgang aus dem Importfile übernommen.
                    Die Beteiligten-Auswahl unten dient zur Mandanten-Zuordnung.
                  </Alert>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Abwicklung
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={usePhasesFromImport}
                        onChange={(_, checked) =>
                          setUsePhasesFromImport(checked)
                        }
                      />
                    }
                    label={
                      usePhasesFromImport
                        ? 'Abwicklung aus Importfile übernehmen'
                        : 'Standard-Abwicklung der gewählten Vorgangsart verwenden'
                    }
                  />
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Aktiv: Der Vollzug wird aus dem DiNo-Export übernommen.
                    Inaktiv: Der Vollzug wird über die (neue) Vorgangsart
                    erstellt.
                  </Typography>
                </Paper>
              )}
              <ParticipantsSection
                participants={participants}
                decisions={decisions}
                selectedTempId={selectedTempId}
                onSelect={setSelectedTempId}
                onResetDecision={handleResetDecision}
                availableRoleTemplates={availableRoleTemplates}
                rolesByTempId={rolesByTempId}
                isTransactionTypeSelected={Boolean(idLegalTransactionType)}
                onRoleChange={(tempId, idLegalRoleTemplate) =>
                  setRolesByTempId(prev => ({
                    ...prev,
                    [tempId]:
                      idLegalRoleTemplate === null
                        ? undefined
                        : idLegalRoleTemplate,
                  }))
                }
              />
              {selectedParticipant ? (
                <Box
                  sx={{
                    border: 2,
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  {/* Klare Kopfzeile, auf welchen Beteiligten sich die
                      Abgleichsfläche bezieht — behebt die Unklarheit bei
                      mehreren Mandanten (#1026). */}
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                      Abgleich für:{' '}
                      {(selectedParticipant.idLegalClientType_hint ===
                      'Juristische Person'
                        ? selectedParticipant.LastName
                        : [
                            selectedParticipant.FirstName,
                            selectedParticipant.LastName,
                          ]
                            .filter(Boolean)
                            .join(' ')) || '(ohne Namen)'}
                      {selectedParticipant.role
                        ? ` · ${selectedParticipant.role}`
                        : ''}
                    </Typography>
                  </Box>
                  <ClientMatchSection
                    idDialogInbox={idDialogInbox}
                    participant={selectedParticipant}
                    decision={decisions[selectedParticipant.tempId]}
                    onDecide={handleDecide}
                    onResetDecision={handleResetDecision}
                  />
                </Box>
              ) : (
                <Alert severity='info'>
                  Bitte wählen Sie oben einen Beteiligten aus, um die
                  Mandantensuche zu starten.
                </Alert>
              )}
              {dialogRealEstate && (
                <RealEstateMatchSection
                  idDialogInbox={idDialogInbox}
                  realEstate={dialogRealEstate}
                  decision={realEstateDecision}
                  onDecide={d => {
                    setRealEstateDecision(d);
                    showAlert({
                      severity: 'success',
                      text: `Immobilie: ${ACTION_LABEL_REAL_ESTATE[d.action]}`,
                    });
                  }}
                  onReset={() => {
                    setRealEstateDecision(undefined);
                    showAlert({
                      severity: 'info',
                      text: 'Immobilien-Entscheidung zurückgesetzt.',
                    });
                  }}
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button onClick={onClose} variant='outlined'>
            Schließen
          </Button>
          {dialogInboxDetail?.Status === 'ignored' ? (
            <Button
              color='primary'
              variant='outlined'
              disabled={isReopening}
              onClick={handleReopen}
            >
              Ignorieren rückgängig machen
            </Button>
          ) : (
            <Button
              color='warning'
              variant='outlined'
              disabled={isReadOnly || isIgnoring}
              onClick={() => setConfirmIgnoreOpen(true)}
            >
              Dialog ignorieren
            </Button>
          )}
          <Button
            variant='contained'
            disabled={
              isReadOnly ||
              !allDecided ||
              !hasAtLeastOneClient ||
              !idLegalTransactionType ||
              !allRolesAssigned ||
              !header.title.trim() ||
              !header.notaryIdUser ||
              !header.legalTransactionStateId ||
              !header.legalTransactionProgressId ||
              isProcessing
            }
            onClick={handleProcess}
          >
            Vorgang erstellen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmIgnoreOpen}
        onClose={() => setConfirmIgnoreOpen(false)}
        maxWidth='xs'
      >
        <DialogTitle>Dialog ignorieren?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            Wollen Sie diesen eingegangenen Dialog wirklich ignorieren? Sie
            finden ihn ggf. wieder, wenn Sie den Schieberegler im Importbereich
            auf „Erledigte anzeigen" setzen. Dort können Sie das Ignorieren auch
            wieder rückgängig machen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmIgnoreOpen(false)}>Abbrechen</Button>
          <Button
            color='warning'
            variant='contained'
            disabled={isIgnoring}
            onClick={handleIgnoreAll}
          >
            Ja
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
